import { useState, type CSSProperties } from "react";
import { Link } from "@tanstack/react-router";
import { Icon } from "@/components/ui/Icon";
import { RauteMark } from "@/components/ui/RauteMark";
import { DateChip } from "@/components/memo/ui/DateChip";
import { StatusBadge, type MemoBadgeStatus } from "@/components/memo/ui/StatusBadge";
import { ghostButtonStyle } from "@/components/memo/ui/ProtokolleHeader";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { formatMemoDate } from "@/lib/format";
import {
  groupByYear,
  hasCommentTarget,
  isUnfinalizedPast,
  isYearOpen,
  loadYearOpenState,
  monthTint,
  tintForMonth,
  monthsPresent,
  relativeMeetingLabel,
  saveYearOpenState,
  splitMemosByDate,
  type LocalNow,
} from "@/lib/memoListGroups";
import { t } from "@/lib/strings";
import type { MemoSummary } from "@/lib/types";

interface SeriesMemoListProps {
  seriesId: string;
  memos: MemoSummary[];
  /** The viewer's local day + minute (`useLocalNow()`), injected so tests can pin it. */
  now: LocalNow;
  /** The series' default start time ("HH:MM:SS"), used when a memo has none of its own.
   *  Required (null = none) so a caller can't silently drop the wiring. */
  defaultStartTime: string | null;
  /** May this viewer create the next Protokoll (can_admin ∪ can_edit_finalized)? */
  canCreate: boolean;
  onNewMemo: () => void;
}

// Human "N Abschnitte · N offene Aufgabe(n) · N Teilnehmer erfasst" summary
// line for a protocol row — singular/plural on the open-task count.
function memoSummaryLine(memo: MemoSummary): string {
  const openWord =
    memo.open_task_count === 1 ? t.memoSummaryOpenTaskSingular : t.memoSummaryOpenTaskPlural;
  const sectionWord =
    memo.section_count === 1 ? t.memoSummarySectionSingular : t.memoSummarySections;
  return [
    `${memo.section_count} ${sectionWord}`,
    `${memo.open_task_count} ${openWord}`,
    `${memo.participant_count} ${t.memoSummaryParticipants}`,
  ].join(" · ");
}

const countStyle: CSSProperties = {
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: "normal",
  textTransform: "none",
  color: "var(--ink-3)",
  background: "var(--surface-soft)",
  borderRadius: 999,
  padding: "1px 7px",
};

function SectionHeading({ label, count }: { label: string; count?: number }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 11.5,
        fontWeight: 600,
        letterSpacing: ".1em",
        textTransform: "uppercase",
        color: "var(--ink-3)",
      }}
    >
      {label}
      {count ? <span style={countStyle}>{count}</span> : null}
    </div>
  );
}

function memoBadge(memo: MemoSummary, todayKey: string): MemoBadgeStatus {
  if (memo.status === "finalized") return "finalized";
  return isUnfinalizedPast(memo, todayKey) ? "unfinalized" : "draft";
}

function MemoRow({
  memo,
  todayKey,
  upcoming,
}: {
  memo: MemoSummary;
  todayKey: string;
  upcoming: boolean;
}) {
  const rel = upcoming ? relativeMeetingLabel(memo.meeting_date, todayKey) : null;
  return (
    <Link
      to="/memo/$memoId"
      params={{ memoId: memo.id }}
      data-testid="memo-row"
      data-upcoming={upcoming ? "true" : "false"}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "12px 16px",
        border: "1px solid var(--hairline)",
        // An upcoming meeting is marked by the accent edge + the relative label, never
        // by the date chip — the chip's colour means the MONTH. Padding shrinks by the
        // extra border width so the content stays on the same left edge as past rows.
        ...(upcoming ? { borderLeft: "3px solid var(--accent)", paddingLeft: 14 } : {}),
        borderRadius: 10,
        textDecoration: "none",
        background: "var(--surface)",
      }}
    >
      <DateChip date={memo.meeting_date} tint={monthTint(memo.meeting_date)} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 15,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: "var(--ink-1)",
            }}
          >
            {formatMemoDate(memo.meeting_date)}
          </span>
          {rel ? (
            <span
              data-testid="memo-row-relative"
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: "var(--accent)",
                background: "var(--accent-soft)",
                borderRadius: 999,
                padding: "3px 10px",
                whiteSpace: "nowrap",
              }}
            >
              {rel}
            </span>
          ) : null}
          <StatusBadge status={memoBadge(memo, todayKey)} />
        </div>
        <div style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 3 }}>
          {memoSummaryLine(memo)}
        </div>
      </div>
      <Icon name="chevRight" size={16} color="var(--ink-4)" />
    </Link>
  );
}

function YearHeader({
  year,
  memos,
  open,
  showMonths,
  onToggle,
}: {
  year: string;
  memos: MemoSummary[];
  open: boolean;
  showMonths: boolean;
  onToggle: () => void;
}) {
  const count = `${memos.length} ${memos.length === 1 ? t.seriesMetaMemosSingular : t.seriesMetaMemos}`;
  return (
    <button
      type="button"
      aria-expanded={open}
      data-testid={`year-${year}`}
      onClick={onToggle}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        color: "var(--ink-2)",
        ...(open
          ? { background: "transparent", border: "none", padding: "6px 2px" }
          : {
              background: "var(--surface)",
              border: "1px solid var(--hairline)",
              borderRadius: 10,
              padding: "11px 16px",
            }),
      }}
    >
      <Icon name={open ? "chevDown" : "chevRight"} size={15} color="var(--ink-3)" />
      <span
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: "var(--ink-1)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {year}
      </span>
      <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>{count}</span>
      {open ? (
        <span style={{ flex: 1, height: 1, background: "var(--hairline)" }} />
      ) : showMonths ? (
        // A collapsed year shows which months it holds, in their month colours.
        <span
          aria-hidden
          data-testid={`year-months-${year}`}
          style={{ display: "flex", gap: 3, marginLeft: "auto" }}
        >
          {monthsPresent(memos).map((mo) => (
            <RauteMark key={mo} size={8} color={tintForMonth(mo).dot} />
          ))}
        </span>
      ) : null}
    </button>
  );
}

/**
 * A Besprechungsreihe's Protokolle, split into "Anstehend" (not started yet, nearest
 * first) and "Vergangen" (started or over, newest first) — today's meeting moves across
 * at its start time or when it is finalized. Once the past spans two or more years it is
 * grouped by year; only the newest year starts open, and each viewer's open/closed
 * choice is remembered per series. Every date chip carries its month's colour.
 */
export function SeriesMemoList({
  seriesId,
  memos,
  now,
  defaultStartTime,
  canCreate,
  onNewMemo,
}: SeriesMemoListProps) {
  const todayKey = now.day;
  const isDesktop = useBreakpoint("md");
  // Tagged with the series it was loaded for, so a route switch straight from one
  // Reihe to another (same component instance) reads the new series' choices instead
  // of carrying the old ones over — correct by construction, no remount key needed.
  const [stored, setStored] = useState(() => ({
    seriesId,
    open: loadYearOpenState(seriesId),
  }));
  const yearState = stored.seriesId === seriesId ? stored.open : loadYearOpenState(seriesId);

  if (memos.length === 0) {
    return (
      <div style={{ color: "var(--ink-3)", fontSize: 14, padding: "40px 0", textAlign: "center" }}>
        {t.seriesNoMemos}
      </div>
    );
  }

  const { upcoming, past } = splitMemosByDate(memos, now, defaultStartTime);
  const years = groupByYear(past);
  const showNoTargetHint = !hasCommentTarget(memos, todayKey);

  const toggleYear = (year: string, open: boolean) => {
    const next = { ...yearState, [year]: !open };
    setStored({ seriesId, open: next });
    saveYearOpenState(seriesId, next);
  };

  const section: CSSProperties = { display: "flex", flexDirection: "column", gap: 10 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      {upcoming.length > 0 || showNoTargetHint ? (
        <div style={section} data-testid="series-upcoming">
          <SectionHeading label={t.seriesUpcoming} count={upcoming.length} />
          {upcoming.map((m) => (
            <MemoRow key={m.id} memo={m} todayKey={todayKey} upcoming />
          ))}
          {showNoTargetHint ? (
            // No draft dated today or later = board comments have nowhere to land yet
            // (they are replayed into the next Protokoll when it is created). Same rule
            // as the backend — by date, not by the start-time split above.
            <div
              data-testid="series-no-upcoming"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
                border: "1px dashed var(--hairline-strong)",
                borderRadius: 10,
                padding: "12px 16px",
              }}
            >
              <Icon name="date" size={20} color="var(--ink-3)" />
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-1)" }}>
                  {t.seriesNoUpcomingTitle}
                </div>
                <div style={{ fontSize: 13, color: "var(--ink-2)" }}>{t.seriesNoUpcomingBody}</div>
              </div>
              {canCreate ? (
                <button type="button" onClick={onNewMemo} style={ghostButtonStyle}>
                  <Icon name="plus" size={15} color="var(--ink-2)" />
                  {t.seriesNewMemo}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {past.length > 0 ? (
        <div style={section} data-testid="series-past">
          <SectionHeading label={t.seriesPast} count={past.length} />
          {years.length < 2
            ? past.map((m) => <MemoRow key={m.id} memo={m} todayKey={todayKey} upcoming={false} />)
            : years.map((g, idx) => {
                const open = isYearOpen(yearState, g.year, idx === 0);
                return (
                  <div key={g.year} style={section}>
                    <YearHeader
                      year={g.year}
                      memos={g.memos}
                      open={open}
                      showMonths={isDesktop}
                      onToggle={() => toggleYear(g.year, open)}
                    />
                    {open
                      ? g.memos.map((m) => (
                          <MemoRow key={m.id} memo={m} todayKey={todayKey} upcoming={false} />
                        ))
                      : null}
                  </div>
                );
              })}
        </div>
      ) : null}
    </div>
  );
}
